import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiProfileProxy } from "@/generated/models/ApiProfileProxy";
import type { ApiUpdateWaveRequest } from "@/generated/models/ApiUpdateWaveRequest";
import type { ApiWave } from "@/generated/models/ApiWave";
import { commonApiPost } from "@/services/api/common-api";
import { CreateWaveStepStatus } from "@/types/waves.types";

export const getCreateWaveStepStatus = ({
  stepIndex,
  activeStepIndex,
}: {
  readonly stepIndex: number;
  readonly activeStepIndex: number;
}): CreateWaveStepStatus => {
  if (stepIndex < activeStepIndex) {
    return CreateWaveStepStatus.DONE;
  }
  if (stepIndex === activeStepIndex) {
    return CreateWaveStepStatus.ACTIVE;
  }
  return CreateWaveStepStatus.PENDING;
};

const getPeriodUpdate = <T>(
  period: T | null | undefined
): Partial<{ readonly period: T }> => {
  if (period === null || period === undefined) {
    return {};
  }

  return { period };
};

const getSlowModeUpdate = (
  slowModeCooldownMs: number | null | undefined
): Partial<{ readonly slow_mode_cooldown_ms: number }> => {
  if (slowModeCooldownMs === null || slowModeCooldownMs === undefined) {
    return {};
  }

  return { slow_mode_cooldown_ms: slowModeCooldownMs };
};

export const convertWaveToUpdateWave = (
  wave: ApiWave
): ApiUpdateWaveRequest => ({
  name: wave.name,
  picture: wave.picture,
  voting: {
    scope: {
      group_id: wave.voting.scope.group?.id ?? null,
    },
    credit_type: wave.voting.credit_type,
    credit_category: wave.voting.credit_category,
    creditor_id: wave.voting.creditor?.id ?? null,
    signature_required: !!wave.voting.signature_required,
    ...getPeriodUpdate(wave.voting.period),
    forbid_negative_votes: wave.voting.forbid_negative_votes,
  },
  visibility: {
    scope: {
      group_id: wave.visibility.scope.group?.id ?? null,
    },
  },
  chat: {
    scope: {
      group_id: wave.chat.scope.group?.id ?? null,
    },
    enabled: wave.chat.enabled,
    links_disabled: wave.chat.links_disabled === true,
    ...getSlowModeUpdate(wave.chat.slow_mode_cooldown_ms),
  },
  participation: {
    scope: {
      group_id: wave.participation.scope.group?.id ?? null,
    },
    no_of_applications_allowed_per_participant:
      wave.participation.no_of_applications_allowed_per_participant,
    required_media: wave.participation.required_media,
    required_metadata: wave.participation.required_metadata,
    signature_required: !!wave.participation.signature_required,
    ...getPeriodUpdate(wave.participation.period),
    terms: wave.participation.terms,
  },
  wave: {
    admin_drop_deletion_enabled: wave.wave.admin_drop_deletion_enabled,
    type: wave.wave.type,
    winning_threshold: wave.wave.winning_threshold,
    max_winners: wave.wave.max_winners,
    max_votes_per_identity_to_drop: wave.wave.max_votes_per_identity_to_drop,
    time_lock_ms: wave.wave.time_lock_ms,
    admin_group: {
      group_id: wave.wave.admin_group.group?.id ?? null,
    },
    decisions_strategy: wave.wave.decisions_strategy,
  },
});

export const canEditWave = ({
  connectedProfile,
  activeProfileProxy,
  wave,
}: {
  readonly connectedProfile: ApiIdentity | null;
  readonly activeProfileProxy: ApiProfileProxy | null;
  readonly wave: ApiWave;
}): boolean => {
  if (!connectedProfile?.handle) {
    return false;
  }
  if (!!activeProfileProxy) {
    return false;
  }
  if (wave.author.handle === connectedProfile.handle) {
    return true;
  }
  if (wave.wave.authenticated_user_eligible_for_admin) {
    return true;
  }
  return false;
};

export const createDirectMessageWave = ({
  addresses,
}: {
  readonly addresses: string[];
}): Promise<ApiWave> => {
  return commonApiPost({
    endpoint: "waves/direct-message/new",
    body: {
      identity_addresses: addresses,
    },
  });
};
